/**
 * ریاضیات و قالب‌بندی «نبض بازار» — جدا از کامپوننت، تا عددی آزمون شود
 * =====================================================================
 * هر چه اینجاست ورودی عدد می‌گیرد و خروجی عدد/رشته می‌دهد؛ نه DOM، نه
 * fetch. `tests/pulse.mjs` همین‌ها را مستقیم اجرا می‌کند.
 */

/** ‎$2.14T / $83.5B / $912M / $1,234 — برای اعداد دلاریِ کلان */
export function usd(v: number | null | undefined): string {
  const x = Number(v)
  if (!Number.isFinite(x) || x <= 0) return '—'
  const abs = Math.abs(x)
  const f = (n: number, d: number) =>
    n.toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: 0 })
  if (abs >= 1e12) return `$${f(x / 1e12, 2)}T`
  if (abs >= 1e9) return `$${f(x / 1e9, 1)}B`
  if (abs >= 1e6) return `$${f(x / 1e6, 1)}M`
  return `$${f(x, 0)}`
}

/** رنگ شاخص ترس و طمع — قرمز → زرد → سبز، همان بازه‌های استاندارد */
export function fngColor(v: number): string {
  if (v < 0) return '#8B93AD'
  if (v < 20) return '#FF4D6D'
  if (v < 40) return '#FF8A5C'
  if (v < 60) return '#FFC24D'
  if (v < 80) return '#7ED957'
  return '#00E39A'
}

export const fngLabel = (v: number): string => {
  if (v < 0) return ''
  if (v < 20) return 'ترس شدید'
  if (v < 40) return 'ترس'
  if (v < 60) return 'خنثی'
  if (v < 80) return 'طمع'
  return 'طمع شدید'
}

/**
 * مختصات عقربه گیج نیم‌دایره.
 * ۰ → منتها الیه چپ (۱۸۰ درجه)، ۱۰۰ → راست (۰ درجه). خروجی برای SVG
 * با مرکز (cx, cy) و شعاع r.
 */
export function gaugePoint(value: number, cx: number, cy: number, r: number) {
  const v = Math.max(0, Math.min(100, value))
  const a = Math.PI * (1 - v / 100)          // ۱۸۰ → ۰ درجه
  return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) }
}

/** کمان گیج از صفر تا مقدار — برای پر کردن رنگی */
export function gaugeArc(value: number, cx: number, cy: number, r: number): string {
  const v = Math.max(0, Math.min(100, value))
  if (v <= 0) return ''
  const start = gaugePoint(0, cx, cy, r)
  const end = gaugePoint(v, cx, cy, r)
  const large = v > 50 ? 1 : 0
  return `M${start.x.toFixed(2)},${start.y.toFixed(2)} A${r},${r} 0 ${large} 1 ${end.x.toFixed(2)},${end.y.toFixed(2)}`
}

export type Pt = { t: number; v: number }

/**
 * مسیر خطِ نمودار از نقاط زمانی — با بازه عمودیِ خودکار و کمی حاشیه.
 *
 * حاشیه (پدینگ) عمدی است: اگر کمینه و بیشینه دقیقاً به لبه بچسبند، خطِ
 * تقریباً صاف (مثل دامیننس که در حد دهم درصد نوسان دارد) مثل دیوار
 * بالا-پایین می‌پرد و نوسانِ ناچیز، بزرگ دیده می‌شود.
 */
export function linePath(pts: Pt[], w: number, h: number):
  { d: string; min: number; max: number } {
  const ok = pts.filter((p) => Number.isFinite(p.v) && p.v >= 0)
  if (ok.length < 2) return { d: '', min: 0, max: 0 }
  let min = Math.min(...ok.map((p) => p.v))
  let max = Math.max(...ok.map((p) => p.v))
  const pad = Math.max((max - min) * 0.15, max * 0.002, 1e-9)
  min -= pad; max += pad
  const t0 = ok[0].t
  const t1 = ok[ok.length - 1].t
  const span = Math.max(1, t1 - t0)
  const X = (t: number) => ((t - t0) / span) * w
  const Y = (v: number) => h - ((v - min) / (max - min)) * h
  let d = ''
  for (const p of ok) d += `${d ? ' L' : 'M'}${X(p.t).toFixed(1)},${Y(p.v).toFixed(1)}`
  return { d, min, max }
}

export const REGIMES: Record<string, { fa: string; color: string; desc: string }> = {
  risk_on: { fa: 'ریسک‌پذیری (Risk-On)', color: '#00E39A',
             desc: 'نشانه‌ها به افزایش اشتهای ریسک در بازار کریپتو اشاره دارند' },
  risk_off: { fa: 'ریسک‌گریزی (Risk-Off)', color: '#FF4D6D',
              desc: 'نشانه‌ها به حرکت سرمایه به سمت امنیت اشاره دارند' },
  neutral: { fa: 'خنثی', color: '#FFC24D',
             desc: 'نشانه‌ها هم‌جهت نیستند؛ بازار در وضعیت انتظار است' },
}

export const COUNTRIES: Record<string, { fa: string; flag: string }> = {
  US: { fa: 'آمریکا', flag: '🇺🇸' }, EU: { fa: 'منطقه یورو', flag: '🇪🇺' },
  GB: { fa: 'انگلیس', flag: '🇬🇧' }, CN: { fa: 'چین', flag: '🇨🇳' },
  JP: { fa: 'ژاپن', flag: '🇯🇵' }, CA: { fa: 'کانادا', flag: '🇨🇦' },
  AU: { fa: 'استرالیا', flag: '🇦🇺' }, DE: { fa: 'آلمان', flag: '🇩🇪' },
  FR: { fa: 'فرانسه', flag: '🇫🇷' }, NZ: { fa: 'نیوزیلند', flag: '🇳🇿' },
  CH: { fa: 'سوئیس', flag: '🇨🇭' },
}

export const IMPORTANCE: Record<number, { fa: string; color: string }> = {
  1: { fa: 'کم', color: '#8B93AD' },
  2: { fa: 'متوسط', color: '#FFC24D' },
  3: { fa: 'زیاد', color: '#FF4D6D' },
}

/** ‎+0.2 / −0.3 با علامت درست — برای نمایش surprise */
export function signed(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return ''
  const x = Number(v)
  const s = Math.abs(x) >= 1000
    ? Math.abs(x).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : String(Number(Math.abs(x).toFixed(digits)))
  return `${x > 0 ? '+' : x < 0 ? '−' : ''}${s}`
}

/** فصل آلت‌کوین: برچسب وضعیت از روی عدد */
export function altseasonLabel(v: number): string {
  if (v < 0) return ''
  if (v >= 75) return 'فصل آلت‌کوین'
  if (v >= 60) return 'نزدیک به فصل آلت‌کوین'
  if (v <= 25) return 'فصل بیت‌کوین'
  if (v <= 40) return 'گرایش به بیت‌کوین'
  return 'میانه'
}
